import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useFocusHandleOnClose, useFocusTrap } from '@prophecy/utils/react/focus';
import { useControlledState } from '@prophecy/utils/react/hooks';
import { Arrow, CheckboxItem, Group, ItemIndicator, Portal, RadioGroup, RadioItem, Root, Sub, Trigger } from '@radix-ui/react-dropdown-menu';
import { createContext, useContext } from 'react';
import { ChevronRightIcon } from '../Icons';
import { Stack, StackItem } from '../Layout';
import { renderWithTooltip } from '../Tooltip';
import { LabelWithHint } from '../Typography/LabelWithHint';
import { Content, Item, Label, Separator, SubContent, SubTrigger } from './styled';
import { tokens } from './tokens';
import { Size } from './types';
export const TriggerContext = /*#__PURE__*/ createContext(false);
export function useTriggerContext() {
    return useContext(TriggerContext);
}
export function Dropdown({ arrow, disabled, overlay, visible, onVisibleChange, children, overlayClassName, overlayStyle, placement: side = 'bottom', placementOffset: sideOffset = 8, align = 'start', alignOffset, portalled = true, size = Size.md, hint, tooltip, onCloseAutoFocus, onInteractOutside, modal = true }) {
    const [_visible, toggleVisible] = useControlledState({
        value: visible,
        onChange: onVisibleChange
    });
    const container = useFocusTrap(Boolean(_visible));
    const defaultOnCloseAutoFocus = useFocusHandleOnClose(Boolean(_visible));
    const content = /*#__PURE__*/ _jsx(Content, {
        className: overlayClassName,
        style: overlayStyle,
        side: side,
        size: size,
        align: align,
        sideOffset: sideOffset,
        asChild: true,
        alignOffset: alignOffset,
        ref: container,
        tabIndex: -1,
        onCloseAutoFocus: onCloseAutoFocus ?? defaultOnCloseAutoFocus,
        onInteractOutside: onInteractOutside,
        children: /*#__PURE__*/ _jsxs("div", {
            children: [
                arrow && /*#__PURE__*/ _jsx(Arrow, {}),
                overlay
            ]
        })
    });
    let triggerUI = children;
    if (tooltip) {
        triggerUI = /*#__PURE__*/ _jsx(_Fragment, {
            children: renderWithTooltip(triggerUI, tooltip)
        });
    }
    if (hint) {
        triggerUI = /*#__PURE__*/ _jsx(LabelWithHint, {
            hint: hint,
            children: triggerUI
        });
    }
    return /*#__PURE__*/ _jsxs(Root, {
        open: _visible,
        dir: "ltr",
        modal: modal,
        onOpenChange: toggleVisible,
        children: [
            /*#__PURE__*/ _jsx(TriggerContext.Provider, {
                value: true,
                children: /*#__PURE__*/ _jsx(Trigger, {
                    disabled: disabled,
                    asChild: true,
                    children: triggerUI
                })
            }),
            portalled ? /*#__PURE__*/ _jsx(Portal, {
                children: content
            }) : content
        ]
    });
}
function SubMenu({ children, overlay, triggerArrow = true, overlayArrow, placementOffset: sideOffset = 8, alignOffset, open, defaultOpen, onOpenChange, size = Size.md, ...rest }) {
    return /*#__PURE__*/ _jsxs(Sub, {
        open: open,
        defaultOpen: defaultOpen,
        onOpenChange: onOpenChange,
        children: [
            /*#__PURE__*/ _jsx(SubTrigger, {
                ...rest,
                size: size,
                children: /*#__PURE__*/ _jsxs(Stack, {
                    direction: "horizontal",
                    width: "100%",
                    gap: tokens.SubMenu.Arrow.gap,
                    alignY: "center",
                    align: "space-between",
                    children: [
                        /*#__PURE__*/ _jsx(StackItem, {
                            children: children
                        }),
                        /*#__PURE__*/ _jsx(StackItem, {
                            shrink: "0",
                            children: triggerArrow && /*#__PURE__*/ _jsx(ChevronRightIcon, {
                                type: "default",
                                size: "xs"
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(SubContent, {
                size: size,
                sideOffset: sideOffset,
                alignOffset: alignOffset,
                children: [
                    overlay,
                    overlayArrow && /*#__PURE__*/ _jsx(Arrow, {})
                ]
            })
        ]
    });
}
Dropdown.Label = Label;
Dropdown.Item = Item;
Dropdown.Group = Group;
Dropdown.CheckboxItem = CheckboxItem;
Dropdown.RadioGroup = RadioGroup;
Dropdown.RadioItem = RadioItem;
Dropdown.ItemIndicator = ItemIndicator;
Dropdown.Separator = Separator;
Dropdown.SubMenu = SubMenu;
export * from './types';
